import { DARK_TOOLTIP_STYLE } from '@src/components/common/ClipContextTooltip';
import TopicBadge from '@src/components/common/TopicBadge';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import type { FC } from 'react';

type Props = {
  topics: string[];
  storyId: string;
};

/** Topic chips shown inline before collapsing the rest into a "+N" indicator. */
const MAX_VISIBLE_TOPICS = 3;

const StoryTopicsCell: FC<Props> = ({ topics, storyId }) => {
  if (topics.length === 0) {
    return <Empty>—</Empty>;
  }

  const tooltipId = `story-topics-${storyId}`;
  const visible = topics.slice(0, MAX_VISIBLE_TOPICS);
  const overflow = topics.length - visible.length;

  return (
    <>
      <Anchor data-tooltip-id={tooltipId}>
        {visible.map(topic => (
          <InlineChip key={topic}>{topic}</InlineChip>
        ))}
        {overflow > 0 && <Overflow>+{overflow}</Overflow>}
      </Anchor>
      <Tooltip
        id={tooltipId}
        style={DARK_TOOLTIP_STYLE}
        opacity={1}
        place="bottom"
        render={() => (
          <TopicChipList>
            {topics.map(topic => (
              <TopicBadge key={topic}>{topic}</TopicBadge>
            ))}
          </TopicChipList>
        )}
      />
    </>
  );
};

export default StoryTopicsCell;

// Two chips per line, packed left with a small fixed gap. `auto` columns hug
// the chip width so short tags sit close together instead of drifting apart;
// the "+N" indicator takes the next cell.
const Anchor = styled.span`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: 6px 10px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  cursor: default;
`;

const Empty = styled.span`
  color: var(--text-muted);
`;

const Overflow = styled.span`
  justify-self: start;
  font-size: 11px;
  color: var(--text-muted);
`;

const TopicChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-width: 280px;
  /* DARK_TOOLTIP_STYLE strips padding/background, so the surface lives here. */
  padding: var(--spacing-sm);
  background: var(--popover);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: var(--elevation-toast);
`;

// Inline variant of the shared TopicBadge: shrinks and ellipsises so three
// chips + the "+N" stay within the column. Full topic text is always available
// in the tooltip. Fills its grid cell and truncates, so two chips always fit
// with a gap and the right-hand one is never cut off.
const InlineChip = styled(TopicBadge)`
  display: block;
  min-width: 0;
  padding: 2px 8px;
  font-size: 11px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
`;
